import { formatPesoAmount } from './money';
import { getTemporaryPriceTotals } from './temporaryPriceCore';
import { computeFormatOfferSubtotal, getFormatOfferId } from './formatOffers';

export const resolveReceiptLinePricing = (item) => {
  const quantity = Number(item?.quantity || 0);
  const originalPrice = Number(item?.price || 0);
  const temporary = getTemporaryPriceTotals(item);
  const isFormatOffer = item?.promotionSnapshot?.kind === 'format_offer' && Boolean(getFormatOfferId(item));
  const hasPromotion = !isFormatOffer && !temporary && item?.discountSource === 'promotion'
    && Number(item?.promotionDiscountAmount || 0) > 0;
  const savedSubtotal = item?.subtotal != null && item.subtotal !== '' && Number.isFinite(Number(item.subtotal))
    ? Number(item.subtotal) : null;
  const baseSubtotal = temporary ? temporary.baseSubtotal
    : isFormatOffer ? savedSubtotal ?? computeFormatOfferSubtotal(originalPrice, quantity)
      : Math.floor(originalPrice * quantity);
  const discountAmount = temporary ? temporary.discountAmount : isFormatOffer ? 0
    : hasPromotion ? Math.floor(Number(item.promotionDiscountAmount))
      : Math.floor(Number(item?.discount || 0) * quantity);
  return {
    baseSubtotal,
    discountAmount,
    subtotal: Math.max(baseSubtotal - discountAmount, 0),
    unitPrice: (temporary || isFormatOffer) && quantity > 0 ? baseSubtotal / quantity : originalPrice,
    hasPromotion,
  };
};

export const formatVoucherQuantity = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return value == null ? '0' : String(value);
  }
  if (Number.isInteger(parsed)) {
    return String(parsed);
  }
  return String(value);
};

export const normalizeReceiptLabel = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

export const stripVarietyPrefix = (productName, varietyName) => {
  const productLabel = normalizeReceiptLabel(productName);
  const varietyLabel = normalizeReceiptLabel(varietyName);
  if (!productLabel || !varietyLabel) return varietyLabel;
  const productLower = productLabel.toLowerCase();
  const varietyLower = varietyLabel.toLowerCase();
  if (!varietyLower.startsWith(productLower)) return varietyLabel;
  let stripped = varietyLabel.slice(productLabel.length).trim();
  stripped = stripped.replace(/^[-–—:|/]+\s*/, '');
  return stripped || varietyLabel;
};

export const buildReceiptItemDisplayName = (item) => {
  const productName = normalizeReceiptLabel(item?.productName);
  const varietyName = normalizeReceiptLabel(item?.varietyName);
  if (productName && varietyName) {
    const strippedVariety = stripVarietyPrefix(productName, varietyName);
    if (!strippedVariety || strippedVariety.toLowerCase() === productName.toLowerCase()) {
      return productName;
    }
    return `${productName} - ${strippedVariety}`;
  }
  return productName || varietyName || 'Producto';
};

export const buildReceiptItemLine = ({ item, unitPrice, quantityLabel }) => {
  const displayName = buildReceiptItemDisplayName(item);
  const metricUnit = normalizeReceiptLabel(item?.metricUnit) || 'unidad';
  const priceText = formatPesoAmount(Number(unitPrice || 0));
  return `${displayName} ${metricUnit} (${priceText} x ${quantityLabel})`;
};

export const formatReceiptMoney = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0';
  return formatPesoAmount(amount);
};

export const CATEGORY_TOTAL_BARCODE_PREFIX = '9';
export const CATEGORY_TOTAL_BARCODE_AMOUNT_SCALE = 1;
export const CATEGORY_TOTAL_BARCODE_MAX_CATEGORY_ID = 99999;
export const CATEGORY_TOTAL_BARCODE_MAX_AMOUNT = 999999;
export const CATEGORY_TOTAL_BARCODE_LENGTH = 12;

export const getEan13CheckDigit = (digits12) => {
  if (!/^\d{12}$/.test(digits12)) return null;
  let sum = 0;
  for (let index = 0; index < digits12.length; index += 1) {
    const digit = Number(digits12[index]);
    sum += index % 2 === 0 ? digit : digit * 3;
  }
  return (10 - (sum % 10)) % 10;
};

export const isValidEan13 = (value) => {
  const normalized = String(value || '').trim();
  if (!/^\d{13}$/.test(normalized)) return false;
  return getEan13CheckDigit(normalized.slice(0, 12)) === Number(normalized[12]);
};

export const buildCategoryTotalEan13Barcode = ({ categoryId, amount }) => {
  const parsedCategoryId = Number.parseInt(categoryId, 10);
  if (
    !Number.isInteger(parsedCategoryId)
    || parsedCategoryId <= 0
    || parsedCategoryId > CATEGORY_TOTAL_BARCODE_MAX_CATEGORY_ID
  ) {
    return { isValid: false, reason: 'categoryId' };
  }

  const parsedAmount = Number(amount || 0);
  if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
    return { isValid: false, reason: 'amount' };
  }

  if (parsedAmount > CATEGORY_TOTAL_BARCODE_MAX_AMOUNT) {
    return { isValid: false, reason: 'amount' };
  }

  const encodedAmount = Math.trunc(parsedAmount / CATEGORY_TOTAL_BARCODE_AMOUNT_SCALE);
  const value = [
    CATEGORY_TOTAL_BARCODE_PREFIX,
    String(parsedCategoryId).padStart(5, '0'),
    String(encodedAmount).padStart(6, '0'),
  ].join('');

  return {
    isValid: true,
    value,
    categoryId: parsedCategoryId,
    encodedAmount,
    amountScale: CATEGORY_TOTAL_BARCODE_AMOUNT_SCALE,
  };
};

export const decodeCategoryTotalEan13Barcode = (value) => {
  const normalized = String(value || '').trim();
  if (!/^\d{12}$/.test(normalized)) return { isValid: false, reason: 'format' };
  if (normalized[0] !== CATEGORY_TOTAL_BARCODE_PREFIX) {
    return { isValid: false, reason: 'prefix' };
  }

  const categoryId = Number.parseInt(normalized.slice(1, 6), 10) || 0;
  if (categoryId <= 0 || categoryId > CATEGORY_TOTAL_BARCODE_MAX_CATEGORY_ID) {
    return { isValid: false, reason: 'categoryId' };
  }
  const encodedAmount = Number.parseInt(normalized.slice(6, 12), 10) || 0;
  return {
    isValid: true,
    value: normalized,
    categoryId,
    encodedAmount,
    amount: encodedAmount * CATEGORY_TOTAL_BARCODE_AMOUNT_SCALE,
    amountScale: CATEGORY_TOTAL_BARCODE_AMOUNT_SCALE,
  };
};

const parseInteger = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
};

const toCurrency = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(Math.floor(parsed), 0) : 0;
};

const resolveReceiptItemSubtotal = (item) => {
  const providedSubtotal = Number(item?.subtotal);
  if (Number.isFinite(providedSubtotal) && providedSubtotal >= 0) {
    return Math.floor(providedSubtotal);
  }

  const quantity = Number(item?.quantity || 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;

  const price = Number(item?.price || 0);
  if (item?.discountSource === 'promotion') {
    const promotionDiscount = Number(item?.promotionDiscountAmount || 0);
    return Math.max(Math.floor(price * quantity) - Math.floor(promotionDiscount), 0);
  }

  const discount = Number(item?.discount || 0);
  return Math.max(Math.floor((price - discount) * quantity), 0);
};

const resolveOrderDiscountAmountForCategoryTotals = ({ purchase, itemsSubtotal }) => {
  const totalAmount = Number(purchase?.totalAmount);
  if (Number.isFinite(totalAmount) && totalAmount >= 0 && totalAmount <= itemsSubtotal) {
    return Math.max(itemsSubtotal - Math.round(totalAmount), 0);
  }

  const percent = Number(purchase?.orderDiscountPercentage);
  if (Number.isFinite(percent) && percent >= 0 && percent <= 100) {
    return Math.min(Math.floor(itemsSubtotal * (percent / 100)), itemsSubtotal);
  }

  const amount = Number(purchase?.orderDiscountAmount || 0);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.min(Math.round(amount), itemsSubtotal);
};

const allocateOrderDiscountByLine = ({ lines, itemsSubtotal, orderDiscountAmount }) => {
  if (!orderDiscountAmount || orderDiscountAmount <= 0 || itemsSubtotal <= 0) {
    return new Map(lines.map((line) => [line.index, 0]));
  }

  const allocations = lines.map((line) => {
    const raw = (line.subtotal / itemsSubtotal) * orderDiscountAmount;
    const base = Math.floor(raw);
    return {
      index: line.index,
      base,
      remainder: raw - base,
    };
  });

  let assigned = allocations.reduce((sum, entry) => sum + entry.base, 0);
  let remaining = Math.max(orderDiscountAmount - assigned, 0);

  allocations
    .sort((a, b) => {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      return a.index - b.index;
    })
    .forEach((entry) => {
      if (remaining <= 0) return;
      entry.base += 1;
      remaining -= 1;
    });

  return new Map(allocations.map((entry) => [entry.index, entry.base]));
};

export const buildReceiptCategoryTotals = ({ purchase, purchaseItems }) => {
  const lines = [];
  const groups = new Map();

  (Array.isArray(purchaseItems) ? purchaseItems : []).forEach((item) => {
    const quantity = Number(item?.quantity || 0);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    const subtotal = resolveReceiptItemSubtotal(item);
    const categoryId = parseInteger(item?.categoryId);
    const label = categoryId === null
      ? 'Sin categoria'
      : normalizeReceiptLabel(item?.categoryName) || `Categoria ${categoryId}`;
    const key = categoryId === null ? 'uncategorized' : `category:${categoryId}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        categoryId: categoryId ?? 0,
        label,
        amount: 0,
      });
    }

    lines.push({
      index: lines.length,
      groupKey: key,
      subtotal,
    });
  });

  const itemsSubtotal = lines.reduce((sum, line) => sum + toCurrency(line.subtotal), 0);
  const orderDiscountAmount = resolveOrderDiscountAmountForCategoryTotals({
    purchase,
    itemsSubtotal,
  });
  const allocations = allocateOrderDiscountByLine({
    lines,
    itemsSubtotal,
    orderDiscountAmount,
  });

  lines.forEach((line) => {
    const group = groups.get(line.groupKey);
    if (!group) return;
    const discount = allocations.get(line.index) || 0;
    group.amount += Math.max(toCurrency(line.subtotal) - discount, 0);
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    barcode: buildCategoryTotalEan13Barcode({
      categoryId: group.categoryId,
      amount: group.amount,
    }),
  }));
};
